import { formatEther } from "viem";
import {
  RiBarChartBoxLine,
  RiDatabase2Line,
  RiLoader4Line,
  RiPercentLine,
} from "@remixicon/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Button } from "@/components/button";
import { Input } from "@/components/input";
import { Badge } from "@/components/badge";
import { useSiloRewardCalculator } from "@/hooks/use-silo-reward-calculator";

function formatUsdFromLrs(lrsAmount: number, lrsUsdPrice: number): string | null {
  if (!Number.isFinite(lrsAmount) || !Number.isFinite(lrsUsdPrice) || lrsUsdPrice <= 0) return null;
  const usd = lrsAmount * lrsUsdPrice;
  if (!Number.isFinite(usd)) return null;
  return usd.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function EthRewardWithUsd({
  ethStr,
  lrsUsdPrice,
  lrsDigits = 4,
  primaryClassName = "font-semibold",
}: {
  ethStr?: string;
  lrsUsdPrice: number;
  lrsDigits?: number;
  primaryClassName?: string;
}) {
  const parsed = Number(ethStr);
  const lrsText = Number.isFinite(parsed) ? parsed.toFixed(lrsDigits) : (0).toFixed(lrsDigits);
  const usdLabel = formatUsdFromLrs(Number.isFinite(parsed) ? parsed : 0, lrsUsdPrice);
  return (
    <div className="space-y-0.5">
      <div className={`tabular-nums ${primaryClassName}`}>{lrsText} LRS</div>
      {usdLabel && (
        <div className="text-xs text-muted-foreground tabular-nums">≈ {usdLabel}</div>
      )}
    </div>
  );
}

function parseBigIntSafe(value: string | number | null | undefined): bigint {
  try {
    return BigInt(value ?? 0);
  } catch {
    return 0n;
  }
}

function formatStakeFromWei(value?: number | string) {
  try {
    return Number(formatEther(parseBigIntSafe(value ?? 0))).toFixed(4);
  } catch {
    return "0.0000";
  }
}

function formatEthValue(value?: string, digits = 4) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return (0).toFixed(digits);
  return parsed.toFixed(digits);
}

type SiloRewardCalculatorPanelProps = {
  lrsUsdPrice: number;
};

export function SiloRewardCalculatorPanel({ lrsUsdPrice }: SiloRewardCalculatorPanelProps) {
  const {
    stakeAmount,
    setStakeAmount,
    storage,
    setStorage,
    storageUnit,
    setStorageUnit,
    uptimePercent,
    setUptimePercent,
    loading,
    error,
    result,
    calculate,
  } = useSiloRewardCalculator();

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 mb-6">
      <Card className="xl:col-span-2 bg-sidebar border border-border hover:border-[#FF8A3C]/40 transition-colors">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RiBarChartBoxLine className="size-5 text-[#FF8A3C]" />
            Silo Node Reward Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Stake Amount (LRS)</label>
            <Input
              type="number"
              value={stakeAmount}
              onChange={(e) => setStakeAmount(e.target.value)}
              placeholder="e.g. 2.0"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Storage</label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={storage}
                onChange={(e) => setStorage(e.target.value)}
                placeholder="e.g. 6.2"
              />
              <select
                value={storageUnit}
                onChange={(e) => setStorageUnit(e.target.value as "gb" | "tb" | "pb")}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="gb">GB</option>
                <option value="tb">TB</option>
                <option value="pb">PB</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Uptime % (optional)</label>
            <Input
              value={uptimePercent}
              onChange={(e) => setUptimePercent(e.target.value)}
              placeholder="0..100"
            />
          </div>

          <div className="rounded-md border border-border bg-background/30 p-3 text-xs text-muted-foreground">
            If uptime is not provided, backend uses current network-average uptime from synced nodes.
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90 cursor-pointer"
              onClick={() => void calculate()}
              disabled={loading}
            >
              {loading ? <RiLoader4Line className="size-4 animate-spin" /> : "Calculate Rewards"}
            </Button>
          </div>

          {error && <div className="text-sm text-red-400">{error}</div>}
        </CardContent>
      </Card>

      <div className="xl:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="bg-sidebar border border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <RiDatabase2Line className="size-4" />
              Hourly Equivalent
            </div>
            <EthRewardWithUsd
              ethStr={result?.estimatedEarnings?.hourlyEquivalent?.eth}
              lrsUsdPrice={lrsUsdPrice}
              lrsDigits={6}
              primaryClassName="text-2xl font-semibold text-[#FF8A3C]"
            />
          </CardContent>
        </Card>

        <Card className="bg-sidebar border border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <RiPercentLine className="size-4" />
              Uptime Used
            </div>
            <div className="text-2xl font-semibold">{result?.assumptions?.uptimePercentUsed ?? 0}%</div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Compared against {result?.assumptions?.comparedAgainstNodes ?? 0} nodes
            </div>
          </CardContent>
        </Card>

        <Card className="bg-sidebar border border-border md:col-span-2">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-2">Estimated Earnings</div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="rounded-md border border-[#FF8A3C]/30 bg-[#FF8A3C]/10 p-3">
                <div className="text-xs text-muted-foreground mb-1">Weekly</div>
                <EthRewardWithUsd
                  ethStr={result?.estimatedEarnings?.weekly?.eth}
                  lrsUsdPrice={lrsUsdPrice}
                  primaryClassName="font-semibold text-[#FF8A3C]"
                />
              </div>
              <div className="rounded-md border border-border bg-background/30 p-3">
                <div className="text-xs text-muted-foreground mb-1">Monthly</div>
                <EthRewardWithUsd
                  ethStr={result?.estimatedEarnings?.monthly?.eth}
                  lrsUsdPrice={lrsUsdPrice}
                  primaryClassName="font-semibold"
                />
              </div>
              <div className="rounded-md border border-border bg-background/30 p-3">
                <div className="text-xs text-muted-foreground mb-1">Yearly</div>
                <EthRewardWithUsd
                  ethStr={result?.estimatedEarnings?.yearly?.eth}
                  lrsUsdPrice={lrsUsdPrice}
                  primaryClassName="font-semibold"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-sidebar border border-border md:col-span-2 hidden">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-2">Assumptions</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-2">
              <div className="rounded-md border border-border bg-background/30 p-3">
                Distribution interval: {result?.assumptions?.distributionIntervalHours ?? 0}h
              </div>
              <div className="rounded-md border border-border bg-background/30 p-3">
                Distribution cron: {result?.assumptions?.distributionCron || "-"}
              </div>
              <div className="rounded-md border border-border bg-background/30 p-3">
                Weight (Storage/Uptime/Stake): {(result?.assumptions?.weightConfig?.storage ?? 0).toFixed(2)} /{" "}
                {(result?.assumptions?.weightConfig?.uptime ?? 0).toFixed(2)} /{" "}
                {(result?.assumptions?.weightConfig?.stake ?? 0).toFixed(2)}
              </div>
              <div className="rounded-md border border-border bg-background/30 p-3">
                Fresh rewards pool: {formatEthValue(result?.freshUndistributedRewards?.eth)} LRS
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="rounded-md border border-border bg-background/30 p-3">
                Input stake: {formatStakeFromWei(result?.input?.stakeAmount)} LRS
              </div>
              <div className="rounded-md border border-border bg-background/30 p-3">
                Input storage: {result?.input?.storage ?? 0} bytes
              </div>
              <div className="rounded-md border border-border bg-background/30 p-3">
                Input uptime: {result?.input?.uptimePercent ?? result?.assumptions?.uptimePercentUsed ?? 0}%
              </div>
              <div className="rounded-md border border-border bg-background/30 p-3">
                Remainder after input node:{" "}
                {formatEthValue(result?.distributionBreakdown?.remainderAfterInputNode?.eth)} LRS
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-sidebar border border-border md:col-span-2">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">Distribution Breakdown (10 Nodes Only)</div>
              <Badge variant="outline" className="border-[#FF8A3C] text-[#FF8A3C]">
                {result?.distributionBreakdown?.withInputNode?.length ?? 0} nodes
              </Badge>
            </div>
            <div className="pt-1">
              <div className="text-xs text-muted-foreground mb-2">Live Nodes Baseline</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="rounded-md border border-border bg-background/30 p-3 text-sm">
                  Nodes in baseline: {result?.distributionBreakdown?.liveNodesOnly?.length ?? 0}
                </div>
                <div className="rounded-md border border-border bg-background/30 p-3 text-sm space-y-1">
                  <div className="text-xs text-muted-foreground">Top baseline reward</div>
                  <EthRewardWithUsd
                    ethStr={result?.distributionBreakdown?.liveNodesOnly?.[0]?.rewardAmount?.eth}
                    lrsUsdPrice={lrsUsdPrice}
                    primaryClassName="font-semibold text-foreground"
                  />
                </div>
              </div>
            </div>
            {result?.distributionBreakdown?.withInputNode?.length ? (
              <div className="space-y-2">
                {result.distributionBreakdown.withInputNode.slice(0, 10).map((item, index) => {
                  if (item.nodeId !== "__input_node__") {
                    return (
                      <div
                        key={`${item.nodeId || "node"}-${index}`}
                        className="rounded-md border border-border bg-background/30 p-3"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-xs text-muted-foreground">Node</div>
                            <div className="font-semibold text-sm break-all">{item.nodeId || "-"}</div>
                          </div>
                          <div className="text-left sm:text-right sm:min-w-[8rem]">
                            <div className="text-xs text-muted-foreground">Reward</div>
                            <EthRewardWithUsd
                              ethStr={item.rewardAmount?.eth}
                              lrsUsdPrice={lrsUsdPrice}
                              primaryClassName="font-semibold text-[#FF8A3C]"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
                          <div className="rounded border border-border bg-background/40 p-2">
                            Weight {Number(item.weightScore ?? 0).toFixed(4)}
                          </div>
                          <div className="rounded border border-border bg-background/40 p-2">
                            Storage {((item.storage ?? 0) / (1024 * 1024 * 1024)).toFixed(2)} GB
                          </div>
                          <div className="rounded border border-border bg-background/40 p-2">Uptime {item.uptime ?? 0}%</div>
                          <div className="rounded border border-border bg-background/40 p-2">
                            Stake {formatStakeFromWei(item.stake)} LRS
                          </div>
                        </div>
                      </div>
                    );
                  }
                })}
                {result.distributionBreakdown.withInputNode
                  .filter((item) => item.nodeId === "__input_node__")
                  .map((item, index) => (
                    <div
                      key={`${item.nodeId || "node"}-${index}`}
                      className="rounded-md border border-accent bg-background/30 p-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs text-muted-foreground">Node</div>
                          <div className="font-semibold text-sm break-all">This will be your Silo Node</div>
                        </div>
                        <div className="text-left sm:text-right sm:min-w-[8rem]">
                          <div className="text-xs text-muted-foreground">Reward</div>
                          <EthRewardWithUsd
                            ethStr={item.rewardAmount?.eth}
                            lrsUsdPrice={lrsUsdPrice}
                            primaryClassName="font-semibold text-[#FF8A3C]"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
                        <div className="rounded border border-border bg-background/40 p-2">
                          Weight {Number(item.weightScore ?? 0).toFixed(4)}
                        </div>
                        <div className="rounded border border-border bg-background/40 p-2">
                          Storage {((item.storage ?? 0) / (1024 * 1024 * 1024)).toFixed(2)} GB
                        </div>
                        <div className="rounded border border-border bg-background/40 p-2">Uptime {item.uptime ?? 0}%</div>
                        <div className="rounded border border-border bg-background/40 p-2">
                          Stake {formatStakeFromWei(item.stake)} LRS
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="rounded-md border border-border bg-background/30 p-3 text-sm text-muted-foreground">
                Run the calculator to see per-node reward distribution.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
